"use client";

import { useTranslations } from "next-intl";
import { FC, useState } from "react";

import Icon from "@/components/ui/Icon/Icon";
import { FAQProps, QAContentProps } from "@/types";

import AccordionV2 from "../Accordion/AccordionV2";
import RichText from "../RichText/RichText";
import FaqBox from "./FaqBox";
import { StructuredData } from "./StructuredData";

function generateFAQSchema(faqs: { question: string; answer: string }[]) {
  if (!faqs?.length) return null;

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq?.answer?.replace(/<[^>]*>/g, ""), // Remove HTML tags
      },
    })),
  };
}

const QAContentComponent: FC<QAContentProps> = ({ entry }) => {
  const closedIcon = <Icon size="24" name="chevron-down" title="open" className="stroke-black" />;
  const openIcon = <Icon size="24" name="chevron-up" title="close" className="stroke-black" />;

  return (
    <>
      {entry.map((item, index) => (
        <AccordionV2
          key={`qa-accordion-${index}`}
          title={
            <>
              <div className="flex flex-row py-s font-secondary font-normal text-m leading-6">{item.question}</div>
            </>
          }
          content={
            <div className="font-secondary font-secondary-light text-m pt-s">
              <RichText content={item.answer} />
            </div>
          }
          defaultOpen={false}
          openIcon={openIcon}
          closedIcon={closedIcon}
          customClassName="border-b border-gray-10"
          customClassNameOpen="border-white"
        />
      ))}
    </>
  );
};

const FAQ: FC<FAQProps> = ({ entry }) => {
  const formattedFAQs = entry?.items?.map((item) => ({
    question: item?.question,
    answer: item?.answer?.html,
  }));

  const faqSchema = generateFAQSchema(formattedFAQs);
  const [activeAccordion, setActiveAccordion] = useState<number | null>(null);
  const t = useTranslations();
  const toggleAccordion = (index: number) => {
    setActiveAccordion(activeAccordion === index ? null : index);
  };

  const closedIcon = <Icon size="24" name="plus" title="open" className="stroke-black" />;
  const openIcon = <Icon size="24" name="minus" title="close" className="stroke-black" />;

  if (entry.variant === "two-levels") {
    return (
      <>
        <div className="w-full">
          {faqSchema && <StructuredData data={faqSchema} />}
          <div className="w-22\/24 md:w-20\/24 mx-auto">
            <div className="uppercase pb-m pt-l">{t("title.common.faq")}</div>
            <div className="w-full pb-l py-m text-black-0">
              {entry.accordionItems?.map((accordion, index) => (
                <AccordionV2
                  key={`faq-accordion-${index}`}
                  title={
                    <>
                      <div className="flex flex-row">
                        <div className="flex items-center pr-m">
                          <Icon className="stroke-black" name={accordion.icon?.link.icon ?? ""} size="40" title={accordion.icon?.label} />
                        </div>
                        <div className="flex flex-col pt-m  ">
                          <div className="font-primary font-medium uppercase leading-4 tracking-widest text-[#222] pb-xs">{accordion.title}</div>
                          <div className="pb-m">{accordion.subtitle}</div>
                        </div>
                      </div>
                    </>
                  }
                  content={<QAContentComponent entry={accordion.items} />}
                  defaultOpen={false}
                  openIcon={openIcon}
                  closedIcon={closedIcon}
                  customClassName="border-b border-gray-10"
                  customClassNameOpen="border-white"
                />
              ))}
            </div>
          </div>
        </div>
      </>
    );
  } else if (entry.variant === "simple" || entry.variant === "inventory") {
    return (
      <>
        {faqSchema && <StructuredData data={faqSchema} />}
        <FaqBox entry={entry} isInventoryVairant={entry.variant === "inventory"} />
      </>
    );
  }
};
export default FAQ;
